import "@/styles/index.css";
import "@/5-shared/utils/imgProtector";

import React from "react";
import { usePageContext } from "vike-react/usePageContext";

import I18nProvider from "@/0-app/providers/I18nProvider";
import { QueryProvider } from "@/0-app/providers/QueryProvider";
import { MobileCTA } from "@/2-sections/chrome/MobileCTA";
import { SiteFooter } from "@/2-sections/chrome/SiteFooter";
import { SiteNav } from "@/2-sections/chrome/SiteNav";
import { SkipLink } from "@/2-sections/chrome/SkipLink";
import { ConsentBanner } from "@/3-features/measurement/consent/ConsentBanner";
import { ErrorBoundary } from "@/3-features/measurement/ErrorBoundary";
import { ownsChrome } from "@/3-features/navigation/routes";
import { ViewportProvider } from "@/3-features/context/ViewportContext";
import { PopupProvider } from "@/3-features/Popup/PopupProvider";

export function Layout({ children }: { children: React.ReactNode }) {
  const pc = usePageContext();
  const path = pc.urlLogical ?? pc.urlPathname ?? "";
  const isEmbed = path.startsWith("/embed");
  const ownChrome = ownsChrome(path);
  // The floating "Contattaci" CTA is redundant (and covers form fields) on the
  // pages that ARE the contact destination.
  const hideMobileCTA =
    path.startsWith("/contact-form") || path.endsWith("/calendly");

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <I18nProvider locale={pc.locale ?? "it"}>
        <QueryProvider>
          <ViewportProvider>
            <PopupProvider>
              {isEmbed ? (
                children
              ) : ownChrome ? (
                <>
                  {children}
                  <ConsentBanner />
                </>
              ) : (
                <>
                  <SkipLink />
                  <SiteNav />
                  <main id="main" className="flex-grow">
                    {children}
                  </main>
                  <SiteFooter />
                  {!hideMobileCTA && <MobileCTA />}
                  <ConsentBanner />
                </>
              )}
            </PopupProvider>
          </ViewportProvider>
        </QueryProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}

function ErrorFallback() {
  const isEn = typeof window !== "undefined" && window.location.pathname.startsWith("/en");
  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-zinc-900">
          {isEn ? "Something went wrong" : "Qualcosa è andato storto"}
        </h1>
        <p className="mt-2 text-zinc-600">
          {isEn ? "Reload the page to try again." : "Ricarica la pagina per riprovare."}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-primary mt-6"
        >
          {isEn ? "Reload" : "Ricarica"}
        </button>
      </div>
    </div>
  );
}
